import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FallbackError } from '@bloomays-lib/ui.shared';
import { styled } from '@mui/material/styles';
import { useQuery } from '@apollo/client';
import {
  GetLegalContractsGQLParams,
  GetLegalContractsWithSummaryGQLResult,
  MissionStatusFilter,
} from '@bloomays-lib/types.shared';
import { GET_LEGAL_CONTRACTS_WITH_SUMMARY } from '@bloomays-lib/adapter.api-bloomer';
import { LoaderPending } from '@bloomays-lib/ui.shared';
import TableLegalContracts, { type SigningStateQuickFilter } from '../organisms/TableLegalContracts';
import { PageHeader } from '../molecules/PageHeader';
import ContractsSummaryBanner from '../molecules/ContractsSummaryBanner';

const Contracts = ({ play = true }: { play?: boolean }) => {
  const { t } = useTranslation(['legalContracts', 'mission']);
  const [status, setStatus] = useState<MissionStatusFilter>(MissionStatusFilter.ACTIVE);
  /** Quick-filter « À signer / Signés » contrôlé (CTA « Voir tout » du bandeau → `'toSign'`). */
  const [signingStateFilter, setSigningStateFilter] = useState<SigningStateQuickFilter>('');

  const {
    loading,
    error,
    data: resultData,
    refetch,
  } = useQuery<GetLegalContractsWithSummaryGQLResult, GetLegalContractsGQLParams>(GET_LEGAL_CONTRACTS_WITH_SUMMARY, {
    variables: { status },
  });

  const data = resultData?.getLegalContractsWithSummary;
  const missions = data?.missions ?? [];

  return (
    <Container>
      <PageHeader
        id="titleAdminDashboardAllContracts"
        title={t('pageTitle')}
        onRefresh={async () => {
          await refetch();
        }}
      >
        <FilterGroupRoot>
          <FilterLabel>{t('statusLabel', { ns: 'mission' })}</FilterLabel>
          <StatusSelect
            value={status}
            aria-label={t('statusLabel', { ns: 'mission' })}
            onChange={(e) => setStatus(e.target.value as MissionStatusFilter)}
          >
            <option value={MissionStatusFilter.ACTIVE}>{t('active', { ns: 'mission' })}</option>
            <option value={MissionStatusFilter.INCOMING}>{t('incoming', { ns: 'mission' })}</option>
            <option value={MissionStatusFilter.FINISHED}>{t('finished', { ns: 'mission' })}</option>
          </StatusSelect>
        </FilterGroupRoot>
      </PageHeader>
      {!loading && !error && data ? (
        <BannerSlot>
          <ContractsSummaryBanner summary={data.summary} onSeeAll={() => setSigningStateFilter('toSign')} />
        </BannerSlot>
      ) : null}
      <TableContainer>
        {loading ? (
          <LoaderPending text={t('fecthingAllContracts')} rotate={play} />
        ) : error ? (
          <FallbackError errorText={t('errorFetchingContracts')}></FallbackError>
        ) : missions.length === 0 ? (
          <NoResult>{t('noMission', { ns: 'mission' })}</NoResult>
        ) : (
          <TableLegalContracts
            missionsContracts={missions}
            signingStateFilter={signingStateFilter}
            onSigningStateFilterChange={setSigningStateFilter}
          />
        )}
      </TableContainer>
    </Container>
  );
};

export default Contracts;

const Container = styled('div')(() => ({
  margin: 'auto',
  width: '90%',
}));

const BannerSlot = styled('div')(() => ({
  marginTop: 16,
}));

const TableContainer = styled('div')(() => ({
  marginTop: 16,
  display: 'flex',
  justifyContent: 'center',
}));

const NoResult = styled('div')(({ theme }) => ({
  width: '80%',
  backgroundColor: theme.palette.red[60],
  height: '200px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const FilterGroupRoot = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 6,
  width: 'fit-content',
}));

const FilterLabel = styled('span')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const StatusSelect = styled('select')(({ theme }) => ({
  height: 38,
  width: 220,
  padding: '0 32px 0 12px',
  borderRadius: 10,
  border: `1px solid ${theme.palette.neutral[15]}`,
  backgroundColor: theme.palette.neutral.white,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239898A8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
}));
